/**
 * Every lift car's state for one page, kept current without new data
 * (twin slice 1, W6 step 9): the pure half of `useLiftColumnState`.
 *
 * ONE READING FOR EVERY SURFACE. The drawer's Guests and Robots lines,
 * the 2D pictogram, the 3D column and the attention list all read a car
 * through `LiftColumnState`, so a car that reads "No reading" in a card
 * can never be drawn solid on the map. The words are `liftReading()`'s
 * and the freshness `liftCarFreshness()`'s (`lift-reading.ts`); this file
 * only keeps the receipts they are measured from and hands the result
 * back with a stable identity.
 *
 * RECEIPTS ARE THIS PAGE'S OWN. A car is live because THIS PAGE saw its
 * row's stamp advance, at least twice, within 10 s, on this device's
 * monotonic clock. Never `Date.now() - lastSeen`: `lastSeen` is the
 * worker's `updated_at_ms`, and any skew between the worker and this
 * device would make a dead car look live or a live one dead. The book is
 * one per site and shared by every mount on the page, so the drawer and
 * the map behind it agree to the receipt.
 *
 * WHEN THE BOOK FORGETS.
 *
 *   - While this page's feed is clamped (W3's FeedHealth: a dropped or
 *     reopened stream, a woken tab, a silent one), every car reads lost
 *     and the book is RESET: what each car had becomes a floor, and only
 *     a row strictly newer than that floor counts again, as a FIRST
 *     sight. So the rows still sitting in the query cache from before
 *     the drop are no evidence, and the replay a reconnect delivers is
 *     "checking", never live, until a second newer row shows the feed
 *     moving. A reset never makes a car more recently heard than it
 *     was: until that newer row arrives the car is checking from its
 *     last real receipt, and lost 30 s after it.
 *   - When the last mount that holds the book lets go, it is emptied: a
 *     page that comes back to the lifts is a new watch, and a row the
 *     cache kept while nobody watched is a first sight.
 *
 * Rows are noted when a mount renders with them, not on the raw stream
 * event: up to one display flush (500 ms) late, and two rows inside one
 * flush count once. Lift rows arrive at about 1 Hz against a 10 s
 * threshold, so neither moves a boundary a person can see.
 *
 * STABLE IDENTITY. `LiftColumnTracker` hands back the SAME entry for a
 * car whose content has not changed, and the same Map when no car
 * changed. Content never includes the row's stamp, so a parked car
 * re-stamped every second re-renders nothing.
 *
 * WHAT A CAR MAY BE DRAWN AS (`LiftLook`, both renderers): solid only
 * while live; dimmed while quiet, checking or off; hollow while lost or
 * absent, with no floor. A car is placed only at the floor its row
 * names, never between floors, and nothing here carries a direction.
 */
import type { LiftDoorState, LiftMotionState, LiftViewModel } from '../../api/adapters';
import type { LiftWriteGate } from '../../api/v1';
import {
  liftCarFreshness,
  liftFreshnessCaveat,
  liftFreshnessNextChangeMs,
  liftReading,
  liftRowStampMs,
  noteLiftReceipt,
  type LiftCarFreshness,
  type LiftFreshness,
  type LiftReading,
  type LiftReceipt,
  type LiftRobotFacts,
  type LiftRobotUse,
} from './lift-reading';

// ----------------------------------------------------------------------------
// The receipt book.
// ----------------------------------------------------------------------------

export class LiftReceiptBook {
  private readonly _receipts = new Map<string, LiftReceipt>();
  /** Per car, what it had at the last reset: the newest stamp counted
   *  and when this page counted it. Only a row strictly newer counts. */
  private readonly _floors = new Map<string, { stampMs: number; atMs: number }>();
  private _holders = 0;

  /**
   * One sight of a car's row stamp at `atMs` on this device's clock.
   * A stamp at or below the car's floor is a row this page already had
   * before the reset, and is ignored. Returns whether the receipt
   * changed.
   */
  note(name: string, stampMs: number, atMs: number): boolean {
    const floor = this._floors.get(name);
    if (floor && !(stampMs > floor.stampMs)) return false;
    const prev = this._receipts.get(name);
    const next = noteLiftReceipt(prev, stampMs, atMs);
    if (!next || next === prev) return false;
    this._receipts.set(name, next);
    return true;
  }

  /**
   * The receipt a car's freshness is measured from. Between a reset and
   * the first newer row, that is the car's last real receipt counted as
   * a first sight (no advances): "checking", and lost 30 s after it.
   */
  receipt(name: string): LiftReceipt | undefined {
    const r = this._receipts.get(name);
    if (r) return r;
    const floor = this._floors.get(name);
    return floor ? { stampMs: floor.stampMs, atMs: floor.atMs, advances: 0 } : undefined;
  }

  /** The feed is clamped: every count is forgotten, every stamp kept as
   *  a floor. Idempotent, so it may run on every clamped render. */
  reset(): void {
    for (const [name, r] of this._receipts)
      this._floors.set(name, { stampMs: r.stampMs, atMs: r.atMs });
    this._receipts.clear();
  }

  /** Forget everything: a new watch. */
  clear(): void {
    this._receipts.clear();
    this._floors.clear();
  }

  /** A mount starts reading the book. The returned release is
   *  idempotent; the last release empties the book. */
  hold(): () => void {
    this._holders += 1;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this._holders -= 1;
      if (this._holders === 0) this.clear();
    };
  }
}

const _bySite = new Map<string, LiftReceiptBook>();

/** The page's lift receipt book for `site`, created on first use. */
export function liftReceiptsFor(site: string): LiftReceiptBook {
  let book = _bySite.get(site);
  if (!book) {
    book = new LiftReceiptBook();
    _bySite.set(site, book);
  }
  return book;
}

/** Tests only: forget every site's book. */
export function _resetLiftReceiptsForTests(): void {
  _bySite.clear();
}

/**
 * Note every live row the page holds, or reset the book while the feed
 * is clamped (and note nothing: a clamped feed is evidence of nothing).
 */
export function observeLiftRows(
  book: LiftReceiptBook,
  rows: readonly LiftViewModel[] | undefined,
  feed: { readonly clamped: boolean } | null | undefined,
  nowMs: number,
): void {
  if (feed?.clamped === true) {
    book.reset();
    return;
  }
  for (const row of rows ?? []) {
    if (row.hasLiveState === false) continue;
    book.note(row.name, liftRowStampMs(row), nowMs);
  }
}

// ----------------------------------------------------------------------------
// One car.
// ----------------------------------------------------------------------------

/** How much a car's drawing may claim. */
export type LiftLookKind =
  /** Live: drawn as the car reports. */
  | 'solid'
  /** Quiet, checking or off: dimmed, never solid. */
  | 'dim'
  /** Lost or absent: dashed edges, no body, no floor. */
  | 'hollow';

/**
 * What either renderer may draw of one car. `LiftCarView` and
 * `LiftColumnState` carry it, so a map of either is a map of looks.
 */
export interface LiftLook {
  readonly look: LiftLookKind;
  /** The floor the car is drawn at, or null for none. Never between
   *  floors: only ever the floor the car's own row names. */
  readonly level: string | null;
  /** The car's door, while live; null otherwise (a stale door state must
   *  not flash as moving). */
  readonly door: LiftDoorState | null;
  /** The car's motion, while live; null otherwise. Never a direction. */
  readonly motion: LiftMotionState | null;
}

export function liftLookKind(state: LiftCarFreshness): LiftLookKind {
  switch (state) {
    case 'live':
      return 'solid';
    case 'quiet':
    case 'checking':
    case 'off':
      return 'dim';
    case 'lost':
    case 'absent':
      return 'hollow';
  }
}

/** One car's freshness and look: everything that needs no building
 *  facts. The 2D map reads only this much. */
export interface LiftCarView extends LiftLook {
  readonly name: string;
  readonly freshness: LiftFreshness;
  /** `liftFreshnessCaveat()`: set while the adapter reports neither of
   *  its own freshness measures. */
  readonly caveat: string | null;
}

export interface LiftCarViewInput {
  readonly name: string;
  readonly row: LiftViewModel | undefined;
  readonly receipt: LiftReceipt | undefined;
  readonly nowMs: number;
  readonly feed?: { readonly clamped: boolean } | null;
}

export function liftCarView({ name, row, receipt, nowMs, feed }: LiftCarViewInput): LiftCarView {
  const freshness = liftCarFreshness({ row, receipt, nowMs, feed });
  const state = freshness.state;
  // Where the car is, only from a reading this page has some trust in.
  // An off car's floor too only while the connector vouches its telemetry
  // is fresh: a disarmed site publishes mode off for every car, link up
  // or down, and the connector keeps sending the edge's retained values
  // while the bridge is offline (`liftReading`, control off).
  const placed =
    state === 'live' ||
    state === 'quiet' ||
    state === 'checking' ||
    (state === 'off' && row?.session?.telemetry_fresh === true);
  const live = state === 'live';
  return {
    name,
    freshness,
    caveat: liftFreshnessCaveat(row),
    look: liftLookKind(state),
    level: placed && row?.currentLevel ? row.currentLevel : null,
    door: live && row ? row.doorState : null,
    motion: live && row ? row.motionState : null,
  };
}

// ----------------------------------------------------------------------------
// The building facts, from W1's asset.
// ----------------------------------------------------------------------------

/** The part of a schema 2.0 asset lift (W1) this file reads. Structural,
 *  so W1's loader type plugs in unchanged. */
export interface AssetLiftFacts {
  readonly name: string;
  readonly map_lane_levels?: readonly string[] | null;
  readonly robot: { readonly use: LiftRobotUse; readonly map_note?: string | null };
}

/**
 * The car list the column state reads, from the asset's lift list
 * (`twinAssetFor(site).lifts`, W1): every car the building has, robot
 * car or not, with the map's own cabin-lane floors and the overlay's
 * note. Nothing here is hardcoded: when a map drop removes a car's
 * lanes and the asset is re-exported, the hedged site row and the ops
 * line go with them.
 */
export function liftFactsFromAsset(
  lifts: readonly AssetLiftFacts[] | null | undefined,
): LiftRobotFacts[] {
  return (lifts ?? []).map((lift) => ({
    name: lift.name,
    use: lift.robot.use,
    mapLaneLevels: lift.map_lane_levels ?? null,
    mapNote: lift.robot.map_note ?? null,
  }));
}

// ----------------------------------------------------------------------------
// Every car, with a stable identity.
// ----------------------------------------------------------------------------

/** One car with its facts and its reading: what the drawer, both
 *  renderers and the attention list read. */
export interface LiftColumnState extends LiftCarView {
  readonly facts: LiftRobotFacts;
  readonly reading: LiftReading;
}

/** Hands back the previous value for a key whose content is unchanged,
 *  and the previous Map when no key changed. */
class StableMap<T> {
  private _entries = new Map<string, { sig: string; value: T }>();
  private _map: ReadonlyMap<string, T> = new Map();

  update(next: readonly (readonly [string, T])[]): ReadonlyMap<string, T> {
    const entries = new Map<string, { sig: string; value: T }>();
    const out = new Map<string, T>();
    const prevKeys = [...this._entries.keys()];
    let changed = false;
    for (const [key, value] of next) {
      if (entries.has(key)) continue;
      const sig = JSON.stringify(value);
      const prev = this._entries.get(key);
      const kept = prev && prev.sig === sig ? prev.value : value;
      if (kept !== prev?.value || prevKeys[entries.size] !== key) changed = true;
      entries.set(key, { sig, value: kept });
      out.set(key, kept);
    }
    if (entries.size !== prevKeys.length) changed = true;
    this._entries = entries;
    if (changed) this._map = out;
    return this._map;
  }
}

export interface LiftViewsInput {
  /** The cars to read, by name. Each is present whether or not it has
   *  a row. */
  readonly names: readonly string[];
  readonly rows: readonly LiftViewModel[] | undefined;
  readonly book: LiftReceiptBook;
  readonly nowMs: number;
  readonly feed: { readonly clamped: boolean } | null | undefined;
}

export interface LiftColumnsInput extends Omit<LiftViewsInput, 'names'> {
  /** Every car the site has, with its building facts. */
  readonly cars: readonly LiftRobotFacts[];
  readonly gate: LiftWriteGate | null | undefined;
  /** The site's zone, for "since HH:MM"; null for the device's. */
  readonly timeZone: string | null;
  /** This device's wall clock, only to name a stamp's day. */
  readonly wallNowMs: number;
}

/** Rows by name, the first of any repeat. A registry-only row is kept:
 *  `liftCarFreshness` reads it as absent. */
function rowsByName(rows: readonly LiftViewModel[] | undefined): Map<string, LiftViewModel> {
  const out = new Map<string, LiftViewModel>();
  for (const row of rows ?? []) if (!out.has(row.name)) out.set(row.name, row);
  return out;
}

export class LiftColumnTracker {
  private readonly _views = new StableMap<LiftCarView>();
  private readonly _columns = new StableMap<LiftColumnState>();

  /** Freshness and looks only, for a surface that has no building facts. */
  views({ names, rows, book, nowMs, feed }: LiftViewsInput): ReadonlyMap<string, LiftCarView> {
    const byName = rowsByName(rows);
    return this._views.update(
      names.map(
        (name) =>
          [
            name,
            liftCarView({ name, row: byName.get(name), receipt: book.receipt(name), nowMs, feed }),
          ] as const,
      ),
    );
  }

  /** Every car in `cars`, with its reading. */
  columns(input: LiftColumnsInput): ReadonlyMap<string, LiftColumnState> {
    const { cars, rows, book, nowMs, feed, gate, timeZone, wallNowMs } = input;
    const byName = rowsByName(rows);
    return this._columns.update(
      cars.map((facts) => {
        const row = byName.get(facts.name);
        const view = liftCarView({
          name: facts.name,
          row,
          receipt: book.receipt(facts.name),
          nowMs,
          feed,
        });
        const reading = liftReading({
          facts,
          row,
          gate,
          freshness: view.freshness,
          timeZone,
          wallNowMs,
        });
        return [facts.name, { ...view, facts, reading }] as const;
      }),
    );
  }
}

/**
 * Milliseconds until any of `names` would change freshness with no new
 * row, for one boundary timer; null when only new data can change them.
 * Null while the feed is clamped: every car is lost until it lifts.
 */
export function liftsNextChangeMs(
  names: readonly string[],
  book: LiftReceiptBook,
  nowMs: number,
  feed: { readonly clamped: boolean } | null | undefined,
): number | null {
  if (feed?.clamped === true) return null;
  let soonest: number | null = null;
  for (const name of names) {
    const ms = liftFreshnessNextChangeMs(book.receipt(name), nowMs);
    if (ms !== null && (soonest === null || ms < soonest)) soonest = ms;
  }
  return soonest;
}
